import { inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, map, of, timeout } from 'rxjs';
import { AgentConfig, AgentCategory } from '../models/agent-config.model';
import { AgentConfigService } from './agent-config.service';
import { AuthenticationService } from '../../services/authentication.service';

/**
 * Resolves an `AgentCategory` to a concrete `AgentConfig`. Holds a live
 * subscription to the offline config stream and caches the latest snapshot so
 * lookups stay synchronous for the AI client and feature layer.
 *
 * Role-aware resolution: a config bound to security roles (via
 * indskr_securityroleassignment) WINS its category for users holding one of
 * those roles, and is invisible to everyone else. A config with no role
 * bindings is the category default.
 */
@Injectable({ providedIn: 'root' })
export class AgentRegistryService {
  #configs: AgentConfig[] = [];
  readonly #configService = inject(AgentConfigService);
  readonly #auth = inject(AuthenticationService);

  constructor() {
    this.#configService.configs$.subscribe(configs => (this.#configs = configs ?? []));
  }

  getAgent(category: AgentCategory): AgentConfig | null {
    return this.#resolve(this.#configs, category);
  }

  isAvailable(category: AgentCategory): boolean {
    return this.getAgent(category) !== null;
  }

  /**
   * The config for `category` once the configurations on the device hold one, or null when none arrives within
   * `waitMs`. A sync stores the configurations before they are read back into the registry, so a caller that runs
   * right after the sync waits here instead of reading the registry's earlier snapshot.
   */
  whenAvailable(category: AgentCategory, waitMs: number): Promise<AgentConfig | null> {
    return firstValueFrom(this.#configService.configs$.pipe(
      map(configs => this.#resolve(configs ?? [], category)),
      filter((agent): agent is AgentConfig => agent !== null),
      timeout({ first: waitMs, with: () => of(null) }),
    ));
  }

  #resolve(configs: AgentConfig[], category: AgentCategory): AgentConfig | null {
    const rows = configs.filter(c => c.category === category);
    if (!rows.length) return null;

    const userRoleIds = new Set<string>(
      (this.#auth.user?.securityRoles ?? []).map((r: { roleid?: string }) => r?.roleid).filter(Boolean) as string[],
    );
    const targeted = rows.find(c => c.roleIds?.some(id => userRoleIds.has(id)));
    if (targeted) return targeted;

    return rows.find(c => !c.roleIds?.length) ?? null;
  }
}
